import { useSelector } from 'react-redux';
import { Box } from '@chakra-ui/react';
import { ApplicationState } from '../../../../../store';
import { CustomHeading } from 'shared-components';
import Field from '../../../../Field/Field';
import { PrevNextButtons } from '../../../../PrevNextButtons';
import { UkAccommodationType } from 'types';
import { useNavigate } from 'react-router-dom';
import ApplicantHeader from '../../../../../components/molecules/applicantHeader';
import { SectionHeader, SectionTitles } from '@/components/molecules/SectionHeader';
import { getApplicationData } from '@/components/pages/applicant-dashboard/sections/helpers/getApplicationData';
import { formatAddress } from '../helpers';
import { useTaskNavigation } from '@/hooks/useTaskNavigation';

export default function ContactDetails() {
  const navigate = useNavigate();
  const { application } = useSelector((state: ApplicationState) => state.apply);
  const { nextRoute, previousRoute } = useTaskNavigation();

  const { initial, current } = getApplicationData(application);

  const {
    residentialAddress,
    residentialPhoneNo,
    contactAddressSameAsResidential,
    termAddressSameAsPermanent,
    contactAddress,
    termAddress,
    hasUkAccommodation,
    ukAccommodationType,
    ukAccommodationPhone,
    ukAccommodationAddress,
  } = current;

  const {
    residentialAddress: initialResidentialAddress,
    residentialPhoneNo: initialResidentialPhoneNo,
    contactAddressSameAsResidential: initialContactAddressSameAsResidential,
    termAddressSameAsPermanent: initialTermAddressSameAsPermanent,
    contactAddress: initialContactAddress,
    termAddress: initialTermAddress,
    hasUkAccommodation: initialHasUkAccommodation,
    ukAccommodationType: initialUkAccommodationType,
    ukAccommodationPhone: initialUkAccommodationPhone,
    ukAccommodationAddress: initialUkAccommodationAddress,
  } = initial;

  const renderContactAddress = () => {
    const showContactAddress = !contactAddressSameAsResidential || !initialContactAddressSameAsResidential;
    if (showContactAddress) {
      const contactAddressReal = contactAddressSameAsResidential ? undefined : contactAddress;
      const initialContactAddressReal = initialContactAddressSameAsResidential ? undefined : initialContactAddress;

      return (
        <Field
          leftLabel="Contact address"
          value={formatAddress(contactAddressReal)}
          originalValue={formatAddress(initialContactAddressReal)}
        />
      );
    }
  };

  const renderTermAddressSection = () => {
    const termAddressDiffersFromPerm = !initialTermAddressSameAsPermanent || !termAddressSameAsPermanent;
    if (termAddressDiffersFromPerm) {
      const termAddressReal = termAddressSameAsPermanent ? undefined : termAddress;
      const initialTermAddressReal = initialTermAddressSameAsPermanent ? undefined : initialTermAddress;

      return (
        <Field
          leftLabel="Term time address"
          value={formatAddress(termAddressReal)}
          originalValue={formatAddress(initialTermAddressReal)}
        />
      );
    }
  };

  const renderAccomodationAddressSection = () => {
    const showUkAccommodation = hasUkAccommodation || initialHasUkAccommodation;
    if (showUkAccommodation) {
      const ukAccomodationAddressReal = hasUkAccommodation ? ukAccommodationAddress : undefined;
      const initialUkAccomodationAddressReal = initialHasUkAccommodation ? initialUkAccommodationAddress : undefined;

      const ukPhoneNumberReal = hasUkAccommodation ? ukAccommodationPhone : undefined;
      const initialUkPhoneNumberReal = initialHasUkAccommodation ? initialUkAccommodationPhone : undefined;

      return (
        <>
          <Field
            leftLabel="Accommodation type"
            value={hasUkAccommodation ? UkAccommodationType[ukAccommodationType] : undefined}
            originalValue={initialHasUkAccommodation ? UkAccommodationType[initialUkAccommodationType] : undefined}
          />
          <Field
            leftLabel="Accomodation phone number"
            value={ukPhoneNumberReal ? `+${ukPhoneNumberReal}` : undefined}
            originalValue={initialUkPhoneNumberReal ? `+${initialUkPhoneNumberReal}` : undefined}
          />
          <Field
            leftLabel="Accomodation address"
            value={formatAddress(ukAccomodationAddressReal)}
            originalValue={formatAddress(initialUkAccomodationAddressReal)}
          />
        </>
      );
    }

    return (
      <Field
        leftLabel="Has UK accommodation"
        value={hasUkAccommodation ?? false}
        originalValue={initialHasUkAccommodation ?? false}
      />
    );
  };

  return (
    <>
      <ApplicantHeader pageName="Application" />
      <Box>
        <SectionHeader title={SectionTitles.CONTACT_DETAILS} />

        <CustomHeading level="h2" mb={2} mt={6} fontSize="md">
          Data
        </CustomHeading>

        <Box>
          <Field
            leftLabel="Country of residential address"
            value={residentialAddress?.country}
            originalValue={initialResidentialAddress?.country}
          />
          <Field
            leftLabel="Permanent residential address"
            value={formatAddress(residentialAddress)}
            originalValue={formatAddress(initialResidentialAddress)}
          />
          <Field
            leftLabel="Home phone number"
            value={residentialPhoneNo ? `+${residentialPhoneNo}` : undefined}
            originalValue={initialResidentialPhoneNo ? `+${initialResidentialPhoneNo}` : undefined}
          />

          {/* Updated address conditional fields to always show, which makes reading the applicant
          information clearer when they have removed or added term addresses, or other */}
          <Field
            leftLabel="Contract address same as residential address"
            value={contactAddressSameAsResidential ?? false}
            originalValue={initialContactAddressSameAsResidential ?? false}
          />
          {renderContactAddress()}

          <Field
            leftLabel="Term address same as permanent address"
            value={termAddressSameAsPermanent ?? false}
            originalValue={initialTermAddressSameAsPermanent ?? false}
          />
          {renderTermAddressSection()}

          {renderAccomodationAddressSection()}
        </Box>

        <Box mt={6}>
          <PrevNextButtons
            onPrevious={previousRoute ? () => navigate(previousRoute) : undefined}
            onNext={nextRoute ? () => navigate(nextRoute) : undefined}
          />
        </Box>
      </Box>
    </>
  );
}
